import {getValueForBreakpoint} from '../utils/breakpoint-util';
import pubsub from '../utils/pubsub';
import screenUtil from '../utils/screen';
import {getValueFromThreshold, isValidThresholdValue} from '../utils/threshold-util';
import Settings from '../domain/Settings';
import Slot from '../domain/Slot';

const DEFAULT_THRESHOLD = 300,
	DEFAULT_DELAY = 30000;

export enum RefreshMode {
	View = 'view',
	Time = 'time',
}

type WaitForTimer = {
	slot: Slot
	timer: ReturnType<typeof setTimeout>
	delay: number
	startTime: number
};

let slotsToRefresh: Slot[],
	waitForInView: Slot[],
	waitForOutOfView: Slot[],
	waitForFirstTimeInView: Slot[],
	waitForTimer: WaitForTimer[],
	mainViewThreshold: BreakpointOverridable<number | string>,
	mainRefreshDelay: BreakpointOverridable<number | string>,
	mainRefreshMaxCPM: number,
	mainRefreshAllowRoadblock: boolean;

export function init(settings: Settings) {
	waitForInView = [];
	waitForOutOfView = [];
	waitForFirstTimeInView = [];
	waitForTimer = [];
	slotsToRefresh = [];

	mainViewThreshold = settings.refreshThreshold ?? DEFAULT_THRESHOLD;
	mainRefreshDelay = settings.refreshDelay ?? DEFAULT_DELAY;
	mainRefreshMaxCPM = settings.refreshConditions?.maxCPM;
	mainRefreshAllowRoadblock = settings.refreshConditions?.allowRoadblock;

	pubsub.subscribe('slot.rendered', _onSlotRendered);
	pubsub.subscribe('scroll', _onScroll);
	document.addEventListener('visibilitychange', _onVisibilityChanged, {passive: true});
}

function _getDelay(slot: Slot) {
	return _getParsedDelay(slot.refreshDelay) ?? _getParsedDelay(mainRefreshDelay) ?? DEFAULT_DELAY;
}

function _getParsedDelay(delay: BreakpointOverridable<number | string>) {
	const delayForBreakpoint = getValueForBreakpoint(delay),
		parsed = parseInt(delayForBreakpoint as string, 10);

	if (typeof parsed !== 'number' || isNaN(parsed)) {
		return null;
	}

	return parsed;
}

function _onSlotRendered(slot: Slot) {
	if (_shouldRefresh(slot)) {
		if (screenUtil.isInView(slot.node, 0)) {
			_handleRefresh(slot);
		} else {
			waitForFirstTimeInView.push(slot);
		}
	}
}

function _shouldRefresh(slot: Slot) {
	if (!getValueForBreakpoint(slot.refreshable)) {
		return false;
	}

	const refreshMaxCPM = getRefreshMaxCPM(slot);
	if (refreshMaxCPM && slot.adResponse?.cpm && (slot.adResponse.cpm) as number > refreshMaxCPM) {
		return false;
	}

	if (!getAllowRoadblock(slot) && slot.adResponse?.isRoadblock) {
		return false;
	}

	if (slot.refreshMediationOnly) {
		return slot.isMediated && !slot.isMediationEmpty;
	}

	return true;
}

function getRefreshMaxCPM(slot: Slot): number | null {
	return slot.refreshConditions?.maxCPM ?? mainRefreshMaxCPM ?? null;
}

function getAllowRoadblock(slot: Slot): boolean {
	return slot.refreshConditions?.allowRoadblock ?? mainRefreshAllowRoadblock ?? true;
}

function _handleRefresh(slot: Slot) {
	// It's already being refreshed
	if (slotsToRefresh.some((st) => st === slot)) {
		return;
	}

	if (_isViewModeRefresh(slot)) {
		waitForOutOfView.push(slot);
	}

	if (_isTimeModeRefresh(slot)) {
		const delay = _getDelay(slot);

		waitForTimer.push({
			slot,
			delay,
			startTime: Date.now(),
			timer: null // Might not start due to being out of view/screen for now
		});

		_updateTimers();
	}

	slotsToRefresh.push(slot);
}

function _doRefresh(slot: Slot, refreshMode: RefreshMode) {
	const idx = slotsToRefresh.findIndex((st) => st === slot);

	if (idx === -1) {
		return;
	}

	waitForTimer = waitForTimer.filter(wft => {
		if (wft.slot === slot) {
			clearTimeout(wft.timer);

			return false;
		}

		return true;
	});

	slotsToRefresh[idx].refresh(refreshMode);
	slotsToRefresh.splice(idx, 1);
}

function _isViewModeRefresh(slot: Slot) {
	return !slot.refreshModes || slot.refreshModes.indexOf(RefreshMode.View) !== -1;
}

function _isTimeModeRefresh(slot: Slot) {
	return slot.refreshModes && slot.refreshModes.indexOf(RefreshMode.Time) !== -1;
}

function _onScroll() {
	waitForOutOfView = waitForOutOfView.filter((slot) => {
		if (!screenUtil.isInView(slot.node, _getSlotThreshold(slot))) {
			waitForInView.push(slot);

			return false;
		}

		return true;
	});

	waitForInView = waitForInView.filter((slot) => {
		if (screenUtil.isInView(slot.node, _getSlotThreshold(slot))) {
			_doRefresh(slot, RefreshMode.View);

			return false;
		}

		return true;
	});

	waitForFirstTimeInView = waitForFirstTimeInView.filter((slot) => {
		if (screenUtil.isInView(slot.node, 0)) {
			_handleRefresh(slot);

			return false;
		}

		return true;
	});

	_updateTimers();
}

function _updateTimers() {
	waitForTimer.forEach((wft) => {
		if (_canRunTimer(wft.slot)) {
			// Only resume stopped timers
			if (wft.timer == null) {
				wft.startTime = Date.now();
				wft.timer = setTimeout(() => {
					_doRefresh(wft.slot, RefreshMode.Time);
				}, wft.delay);
			}
		} else {
			if (wft.timer != null) {
				clearTimeout(wft.timer);
				wft.timer = null;
				wft.delay -= Date.now() - wft.startTime;
			}
		}
	});
}

function _canRunTimer(slot: Slot) {
	return document.visibilityState === 'visible' && screenUtil.isInView(slot.node, 0);
}

function _getSlotThreshold(slot: Slot) {
	const threshold = slot.refreshThreshold ?? mainViewThreshold,
		parsed = getValueFromThreshold(threshold);

	if (!isValidThresholdValue(parsed)) {
		return DEFAULT_THRESHOLD;
	}

	return parsed;
}

function _onVisibilityChanged() {
	_updateTimers();
}
